import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Flex, Avatar, Typography, Col, Select, Row, Divider, Button, message, Input, Switch, Skeleton, Spin, Tooltip, Grid } from 'antd';
import { PlusOutlined, ApiOutlined, ArrowLeftOutlined, FacebookOutlined, InstagramOutlined, WhatsAppOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getAgentContacts } from '../../AgentServices';
import { getChannels, createChannel } from '../../../Channels/ChannelsServices';
import { getCredentials, getCredentialResources, createCredential } from '../../../Credentials/CredentialsServices';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import { isFacebookSDKLoaded, loginWithFacebookSdk } from '../../../../Utils/facebook';
import { facebookBusinessLoginConfigId } from '../../../../Config/facebookConfig';
import { useFacebookSDK } from '../../../../Hooks/useFacebookSDK';

const { Text } = Typography;

const ASSIGNABLE_CHANNEL_FILTERS = { is_virtual: false };
const getAssignableChannels = (channels = []) => (
    channels.filter(channel => !channel.is_virtual)
);

/**
 * PlatformSection component for selecting channels in agent creation/editing.
 * 
 * In the new API:
 * - Agents are associated with channels via `channel_ids` array
 * - Channels can be created inline or selected from existing ones
 * - This section allows selecting existing channels or creating new ones
 */
const PlatformSection = ({ form, mode, id, initialData, onPagesLoadingChange, onPagesReadyChange, typeRef, onChannelChange, onStartChannelCreation, onCancelChannelCreation }) => {
    const { handleError } = useErrorHandler();
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const screens = Grid.useBreakpoint();
    const isMobile = !!screens?.xs;
    const [channels, setChannels] = useState([]);
    const [contacts, setContacts] = useState([]);

    // Pagination and search states
    const [channelsSearch, setChannelsSearch] = useState('');
    const [channelsPage, setChannelsPage] = useState(1);
    const [hasMoreChannels, setHasMoreChannels] = useState(true);
    const [channelsLoading, setChannelsLoading] = useState(false);

    const [contactsSearch, setContactsSearch] = useState('');
    const [contactsPage, setContactsPage] = useState(1);
    const [hasMoreContacts, setHasMoreContacts] = useState(true);
    const [contactsLoading, setContactsLoading] = useState(false);

    // Inline channel creation states
    const [isCreatingChannel, setIsCreatingChannel] = useState(false);
    const [channelCreating, setChannelCreating] = useState(false);
    const [credentials, setCredentials] = useState([]);
    const [credentialsLoading, setCredentialsLoading] = useState(false);
    const [resources, setResources] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(false);
    const [resourcesFetched, setResourcesFetched] = useState(false);
    const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
    const [channelForm] = Form.useForm();

    // Inline credential creation states
    const [isCreatingCredential, setIsCreatingCredential] = useState(false);
    const [credentialCreating, setCredentialCreating] = useState(false);
    const [credentialPendingRetry, setCredentialPendingRetry] = useState(false);
    const [selectedCredentialType, setSelectedCredentialType] = useState('facebook_page');

    // Channel types for credential creation - matches CredentialForm
    const credentialChannelTypes = useMemo(() => [
        { value: 'facebook_page', label: t('facebook', { defaultValue: 'Facebook' }), icon: <FacebookOutlined style={{ color: '#1877F2' }} /> },
        { value: 'instagram', label: `${t('instagram', { defaultValue: 'Instagram' })} (${t('coming_soon')})`, icon: <InstagramOutlined style={{ color: '#E4405F' }} />, disabled: true },
        { value: 'whatsapp', label: `${t('whatsapp', { defaultValue: 'WhatsApp' })} (${t('coming_soon')})`, icon: <WhatsAppOutlined style={{ color: '#25D366' }} />, disabled: true },
    ], [t]);
    useFacebookSDK(2);

    const searchTimeoutRef = useRef();
    const contactsAbortControllerRef = useRef(null);

    // Watch for credential changes in the channel creation form
    const selectedCredentialId = Form.useWatch('credential_id', channelForm);

    // Initialize form fields for edit mode
    useEffect(() => {
        if (initialData?.admin_contacts) {
            form.setFieldsValue({
                admins: initialData.admin_contacts.map((admin) => admin.id),
            });
            setContacts((prev) => (prev.length ? prev : initialData.admin_contacts));
        }
    }, [form, initialData]);

    // In edit mode, hydrate channels field with existing channel associations
    useEffect(() => {
        if (mode === 'edit' && initialData?.channels) {
            const visibleChannels = getAssignableChannels(initialData.channels);
            const channelIds = visibleChannels.map(ch => ch.id);
            form.setFieldsValue({
                channel_ids: channelIds,
            });
            setChannels((prev) => (prev.length ? prev : visibleChannels));
        }
    }, [mode, initialData, form]);

    /**
     * Fetch channels that can be assigned to an agent.
     */
    const fetchChannels = useCallback(async (page = 1, search = '') => {
        try {
            setChannelsLoading(true);

            const { data = [], metadata = {} } = await getChannels({
                page,
                size: 10,
                search: search.trim(),
                filters: ASSIGNABLE_CHANNEL_FILTERS,
                sort_by: 'name',
                sort_direction: 'asc',
            });

            setChannels(prev => page === 1 ? data : [...prev, ...data]);
            setHasMoreChannels(Boolean(metadata.has_next));
            setChannelsPage(page);

            return data;
        } catch (error) {
            handleError(error);
            return [];
        } finally {
            setChannelsLoading(false);
        }
    }, [handleError]);

    const fetchContacts = useCallback(async (page = 1, search = '') => {
        if (!id) return;

        if (contactsAbortControllerRef.current) {
            contactsAbortControllerRef.current.abort();
        }
        contactsAbortControllerRef.current = new AbortController();

        try {
            setContactsLoading(true);
            const { data, metadata } = await getAgentContacts({
                id,
                page,
                size: 10,
                search,
                signal: contactsAbortControllerRef.current.signal
            });
            setContacts(prev => page === 1 ? data : [...prev, ...data]);
            setHasMoreContacts(metadata.has_next);
            setContactsPage(page);

        } catch (error) {
            if (error.name === 'AbortError' || error.message?.includes('abort') || error.code === 'ERR_CANCELED') {
                return; // Ignore abort errors
            }
            handleError(error);
        } finally {
            if (!contactsAbortControllerRef.current.signal.aborted) {
                setContactsLoading(false);
            }
        }
    }, [handleError, id]);

    // Fetch credentials for channel creation
    const fetchCredentials = useCallback(async () => {
        setCredentialsLoading(true);
        try {
            const response = await getCredentials({ page: 1, size: 100 });
            const data = response.data || [];
            setCredentials(data);
            return data;
        } catch (error) {
            handleError(error);
            return [];
        } finally {
            setCredentialsLoading(false);
        }
    }, [handleError]);

    // Fetch resources when credential changes
    useEffect(() => {
        if (!selectedCredentialId) {
            setResources([]);
            setResourcesFetched(true);
            return;
        }

        const fetchResources = async () => {
            setResourcesFetched(false);
            setResourcesLoading(true);
            try {
                const response = await getCredentialResources({
                    credential_id: selectedCredentialId,
                    page: 1,
                    size: 100
                });
                setResources(response.data || []);
            } catch (error) {
                console.error("Failed to fetch resources", error);
                setResources([]);
            } finally {
                setResourcesLoading(false);
                setResourcesFetched(true);
            }
        };

        fetchResources();
    }, [selectedCredentialId]);

    // Initial fetch - run only once on mount
    useEffect(() => {
        const initFetch = async () => {
            const data = await fetchChannels();
            const currentChannelIds = form.getFieldValue('channel_ids') || [];
            const hasExistingChannels = currentChannelIds.length > 0 || (initialData?.channels && initialData.channels.length > 0);

            if (data && data.length === 0 && !hasExistingChannels) {
                // Auto-open creation if user has no channels
                handleStartChannelCreation();
            }
        };
        initFetch();
        if (mode === 'edit') {
            fetchContacts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Propagate readiness (channels loaded) to parent for tour logic
    useEffect(() => {
        if (onPagesReadyChange) onPagesReadyChange(Array.isArray(channels) && channels.length > 0);
    }, [channels, onPagesReadyChange]);

    const handleChannelsSearch = useCallback((search) => {
        clearTimeout(searchTimeoutRef.current);
        setChannelsSearch(search);

        searchTimeoutRef.current = setTimeout(() => {
            fetchChannels(1, search);
        }, 200);
    }, [fetchChannels]);

    const handleContactsSearch = useCallback((search) => {
        clearTimeout(searchTimeoutRef.current);
        setContactsSearch(search);

        searchTimeoutRef.current = setTimeout(() => {
            fetchContacts(1, search);
        }, 200);
    }, [fetchContacts]);

    const handleChannelsScroll = useCallback(async (event) => {
        const { target } = event;
        if (hasMoreChannels && !channelsLoading &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
            await fetchChannels(channelsPage + 1, channelsSearch);
        }
    }, [channelsPage, channelsSearch, hasMoreChannels, channelsLoading, fetchChannels]);

    const handleContactsScroll = useCallback(async (event) => {
        const { target } = event;
        if (hasMoreContacts && !contactsLoading &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
            await fetchContacts(contactsPage + 1, contactsSearch);
        }
    }, [contactsPage, contactsSearch, hasMoreContacts, contactsLoading, fetchContacts]);

    const handleStartChannelCreation = useCallback(async () => {
        setChannelDropdownOpen(false); // Close dropdown first
        setIsCreatingChannel(true);
        setIsCreatingCredential(false);
        channelForm.resetFields();
        channelForm.setFieldsValue({
            is_synchronization_enabled: true,
        });
        onStartChannelCreation && onStartChannelCreation();

        const data = await fetchCredentials();
        if (data && data.length === 0) {
            // Auto open credential creation if user has no credentials
            setIsCreatingCredential(true);
            setSelectedCredentialType('facebook_page');
        }
    }, [fetchCredentials, channelForm, onStartChannelCreation]);

    const handleCancelChannelCreation = useCallback(() => {
        setIsCreatingChannel(false);
        setIsCreatingCredential(false);
        setCredentialPendingRetry(false);
        channelForm.resetFields();
        onCancelChannelCreation && onCancelChannelCreation();
    }, [channelForm, onCancelChannelCreation]);

    const handleStartCredentialCreation = useCallback(() => {
        setIsCreatingCredential(true);
        setSelectedCredentialType('facebook_page');
        setCredentialPendingRetry(false);
    }, []);

    const handleCancelCredentialCreation = useCallback(() => {
        setIsCreatingCredential(false);
        setSelectedCredentialType('facebook_page');
        setCredentialPendingRetry(false);
    }, []);

    /**
     * Handles platform login and automatically creates the credential on success.
     * The user picks the channel type first, then logs in — no separate save step.
     */
    const handleFacebookLogin = useCallback(async () => {
        if (!isFacebookSDKLoaded()) {
            message.error(t('facebook_sdk_not_loaded'));
            return;
        }

        if (!selectedCredentialType) {
            message.error(t('please_select_channel_type_first', { defaultValue: 'Please select a channel type first' }));
            return;
        }

        setCredentialCreating(true);
        setCredentialPendingRetry(false);

        loginWithFacebookSdk(async (response) => {
            try {
                console.log('Facebook login response: ', response);
                if (response.authResponse?.accessToken) {
                    // Auto-save the credential immediately after successful login
                    const credentialResponse = await createCredential({
                        channel_type: selectedCredentialType,
                        short_lived_token: response.authResponse.accessToken,
                    });

                    const newCredential = credentialResponse.data;
                    message.success(t('credential_created_successfully', { defaultValue: 'Credential created successfully' }));

                    // Add to credentials list and auto-select in the channel form
                    setCredentials(prev => [newCredential, ...prev]);
                    channelForm.setFieldsValue({
                        credential_id: newCredential.id,
                        external_channel_id: null,
                    });

                    // Reset credential creation state
                    setIsCreatingCredential(false);
                    setSelectedCredentialType('facebook_page');
                } else if (response.status === 'unknown') {
                    // This happens when FB requires 2FA or user closes the popup prematurely
                    setCredentialPendingRetry(true);
                    message.warning(t('facebook_requires_2fa_short', { defaultValue: 'Please complete the security check in the Facebook window.' }));
                } else {
                    message.error(response.error || t('facebook_login_failed'));
                }
            } catch (error) {
                handleError(error);
            } finally {
                setCredentialCreating(false);
            }
        }, facebookBusinessLoginConfigId);
    }, [handleError, t, selectedCredentialType, channelForm]);

    /**
     * Create a new channel and add it to the selection.
     */
    const handleCreateChannel = useCallback(async () => {
        try {
            const values = await channelForm.validateFields();
            setChannelCreating(true);

            // Derive channel_type from the selected credential
            const selectedCredential = credentials.find(c => c.id === values.credential_id);
            const channelType = selectedCredential?.channel_type || 'facebook_page';

            const response = await createChannel({
                credential_id: values.credential_id,
                channel_type: channelType,
                external_channel_id: values.external_channel_id,
                is_synchronization_enabled: values.is_synchronization_enabled ?? true,
                description: values.description,
            });

            const newChannel = response.data;
            message.success(t('channel_created_successfully'));

            // Add to channels list
            setChannels(prev => [newChannel, ...prev]);

            // Add to selected channel_ids
            const currentChannelIds = form.getFieldValue('channel_ids') || [];
            form.setFieldsValue({
                channel_ids: [...currentChannelIds, newChannel.id],
            });

            // Reset and close the creation form
            setIsCreatingChannel(false);
            channelForm.resetFields();

            // Reopen the dropdown so the user can see the newly added channel
            setTimeout(() => setChannelDropdownOpen(true), 100);

            // Notify parent to re-evaluate button state
            // (form.setFieldsValue doesn't trigger onValuesChange)
            if (onChannelChange) onChannelChange();

        } catch (error) {
            if (error?.errorFields) {
                // Validation error - form will show the errors
                return;
            }
            handleError(error);
        } finally {
            setChannelCreating(false);
        }
    }, [channelForm, form, handleError, t, credentials]);

    const renderChannelOption = useCallback((channel, isManaged) => (
        <Flex align="center" gap={12} style={isManaged ? { opacity: 0.6 } : undefined}>
            <Avatar
                src={channel.picture_url}
                alt={channel.name}
                size="small"
                icon={<ApiOutlined />}
            />
            <Flex vertical gap={0} style={{ minWidth: 0, flex: 1 }}>
                <Text ellipsis dir="auto" style={{ unicodeBidi: 'plaintext' }}>{channel.name || channel.external_channel_id}</Text>
                <Flex gap={4} wrap="wrap">
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {t(channel.channel_type)}
                    </Text>
                    {channel.channel_json?.category && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            • {channel.channel_json.category}
                        </Text>
                    )}
                    {(channel.description || channel.channel_json?.about) && (
                        <Text type="secondary" dir="auto" style={{ fontSize: '11px', opacity: 0.8, marginTop: 2, unicodeBidi: 'plaintext' }} ellipsis>
                            {channel.description || channel.channel_json?.about}
                        </Text>
                    )}
                    {isManaged && (
                        <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                            — {t('linked_agent')}: {channel.agent_name || channel.agent_id}
                        </Text>
                    )}
                </Flex>
            </Flex>
        </Flex>
    ), [t]);

    const renderContactOption = useCallback((item, isAdmin = false) => (
        <Flex align="center" gap={12}>
            <Avatar
                src={item.picture_url}
                alt={isAdmin ? item.full_name : item.name}
                size="small"
            />
            <Flex vertical gap={0}>
                <Text>{isAdmin ? item.full_name : item.name}</Text>
                {isAdmin && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {item.email || item.phone}
                    </Text>
                )}
            </Flex>
        </Flex>
    ), []);

    const channelOptions = useMemo(() =>
        channels.map((channel) => {
            // A channel is "managed" if it has an agent_id that is NOT the current agent
            const isManaged = !!channel.agent_id && channel.agent_id !== id;
            return {
                value: channel.id,
                label: renderChannelOption(channel, isManaged),
                channel: channel,
                disabled: isManaged,
            };
        }),
        [channels, renderChannelOption, id]);

    /**
     * Custom tag render for channel select - shows small avatar with name.
     */
    const renderChannelTag = useCallback(({ label, value, closable, onClose }) => {
        const channel = channels.find(ch => ch.id === value);
        const tagContent = (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 8px 2px 4px',
                    background: isDarkMode ? '#1f1f1f' : '#f5f5f5',
                    border: `1px solid ${isDarkMode ? '#434343' : '#d9d9d9'}`,
                    borderRadius: 4,
                    marginInlineEnd: 4,
                    marginBlock: 2,
                }}
            >
                <Avatar
                    src={channel?.picture_url}
                    size={18}
                    icon={<ApiOutlined style={{ fontSize: 10 }} />}
                />
                <span dir="auto" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', unicodeBidi: 'plaintext' }}>
                    {channel?.name || channel?.external_channel_id || value}
                    {channel?.channel_json?.category && (
                        <span style={{ color: isDarkMode ? '#8c8c8c' : '#8c8c8c', fontSize: '10px', marginLeft: 4 }}>
                            ({channel.channel_json.category})
                        </span>
                    )}
                </span>
                {closable && (
                    <span
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onClose(e);
                        }}
                        style={{ cursor: 'pointer', marginLeft: 4, color: isDarkMode ? '#8c8c8c' : '#595959' }}
                    >
                        ×
                    </span>
                )}
            </span>
        );

        if (!isMobile && (channel?.description || channel?.channel_json?.about)) {
            return (
                <Tooltip title={channel.description || channel.channel_json.about} placement="top">
                    {tagContent}
                </Tooltip>
            );
        }
        return tagContent;
    }, [channels, isDarkMode, isMobile]);

    const renderAdminsOptions = useMemo(() =>
        contacts.map((contact) => ({
            value: contact.id,
            label: renderContactOption(contact, true),
        })),
        [contacts, renderContactOption]);

    const credentialOptions = useMemo(() =>
        credentials.map(cred => ({
            value: cred.id,
            label: `${cred.name || cred.email || cred.credential_user_id || 'Unnamed'} (${t(cred.channel_type)})`,
        })),
        [credentials, t]);

    const renderResourceOption = useCallback((resource) => (
        <Flex align="center" gap={8}>
            <Avatar
                src={resource.picture_url}
                size="small"
                icon={<ApiOutlined />}
            />
            <Text>{resource.name}</Text>
        </Flex>
    ), []);

    const resourceOptions = useMemo(() =>
        resources.map(res => ({
            value: res.id,
            label: renderResourceOption(res),
            name: res.name,
        })),
        [resources, renderResourceOption]);

    return (
        <Row gutter={[16, 0]} style={{ margin: 0 }}>
            <Col xs={24}>
                <Form.Item
                    name="channel_ids"
                    label={t('channels')}
                    rules={[{ required: true, message: t('please_select_channels') }]}
                    style={{ marginBottom: 4 }}
                    tooltip={t('select_channels_tooltip', { defaultValue: 'Select the channels this agent will handle messages from' })}
                >
                    <Select
                        data-tour="platform-type"
                        mode="multiple"
                        placeholder={t('search_channels')}
                        loading={channelsLoading}
                        style={{ width: '100%' }}
                        options={channelOptions}
                        tagRender={renderChannelTag}
                        showSearch
                        filterOption={false}
                        onSearch={handleChannelsSearch}
                        onPopupScroll={handleChannelsScroll}
                        ref={typeRef}
                        open={channelDropdownOpen}
                        onDropdownVisibleChange={setChannelDropdownOpen}
                        dropdownRender={(menu) => (
                            <>
                                {menu}
                                <Divider style={{ margin: '8px 0' }} />
                                <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={handleStartChannelCreation}
                                    block
                                    data-tour="channel-create-btn"
                                >
                                    {t('create_new_channel')}
                                </Button>
                            </>
                        )}
                    />
                </Form.Item>
            </Col>

            {/* Inline Channel Creation Panel */}
            {isCreatingChannel && (
                <Col xs={24}>
                    <div style={{
                        padding: '16px',
                        marginTop: '8px',
                        marginBottom: '16px',
                        border: `1px solid ${isDarkMode ? '#434343' : '#d9d9d9'}`,
                        borderRadius: '8px',
                        background: isDarkMode ? '#1f1f1f' : '#fafafa'
                    }}>
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>{t('create_new_channel')}</Text>
                        </div>

                        <Form form={channelForm} layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} md={isCreatingCredential ? 24 : 12}>
                                    {isCreatingCredential ? (
                                        <div
                                            style={{
                                                padding: '12px 16px',
                                                marginBottom: 16,
                                                borderLeft: `3px solid ${isDarkMode ? '#177ddc' : '#1677ff'}`,
                                                borderRadius: '0 6px 6px 0',
                                                background: isDarkMode ? '#141414' : '#f8fafc',
                                            }}
                                            data-tour="credential-create"
                                        >
                                            <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                                                <Text strong style={{ fontSize: 13 }}>
                                                    {t('create_new_credential', { defaultValue: 'Create New Credential' })}
                                                </Text>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    onClick={handleCancelCredentialCreation}
                                                    style={{ color: isDarkMode ? '#8c8c8c' : '#8c8c8c', fontSize: 12 }}
                                                >
                                                    ✕
                                                </Button>
                                            </Flex>

                                            {/* Step 1: Choose credential type */}
                                            <Flex vertical gap={12}>
                                                <div>
                                                    <Text style={{ fontSize: 12, color: isDarkMode ? '#8c8c8c' : '#595959', marginBottom: 6, display: 'block' }}>
                                                        {t('select_channel_type', { defaultValue: 'Select channel type' })}
                                                    </Text>
                                                    <Select
                                                        placeholder={t('select_channel_type', { defaultValue: 'Select channel type' })}
                                                        value={selectedCredentialType}
                                                        onChange={(value) => setSelectedCredentialType(value)}
                                                        style={{ width: '100%' }}
                                                        options={credentialChannelTypes}
                                                    />
                                                </div>

                                                {/* Step 2: Show login button only after type is selected */}
                                                {selectedCredentialType && (
                                                    credentialCreating ? (
                                                        <Flex
                                                            align="center"
                                                            justify="center"
                                                            gap={8}
                                                            style={{
                                                                padding: '10px 16px',
                                                                borderRadius: 6,
                                                                background: isDarkMode ? '#1a1a2e' : '#f0f5ff',
                                                                border: `1px solid ${isDarkMode ? '#177ddc' : '#91caff'}`,
                                                            }}
                                                        >
                                                            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                                                            <Text style={{ fontSize: 13, color: isDarkMode ? '#91caff' : '#1677ff' }}>
                                                                {t('authenticating_and_saving', { defaultValue: 'Authenticating & saving...' })}
                                                            </Text>
                                                        </Flex>
                                                    ) : credentialPendingRetry ? (
                                                        <div style={{ padding: '12px 16px', background: isDarkMode ? '#2b2111' : '#fffbe6', border: `1px solid ${isDarkMode ? '#876800' : '#ffe58f'}`, borderRadius: 6 }}>
                                                            <Text style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                                                                {t('facebook_requires_2fa', { defaultValue: 'Facebook requires additional security verification. Please complete it in the popup window, then click Continue.' })}
                                                            </Text>
                                                            <Flex gap={8}>
                                                                <Button type="primary" size="small" onClick={handleFacebookLogin} data-tour="credential-login-btn">
                                                                    {t('continue', { defaultValue: 'Continue' })}
                                                                </Button>
                                                                <Button size="small" onClick={() => setCredentialPendingRetry(false)}>
                                                                    {t('cancel', { defaultValue: 'Cancel' })}
                                                                </Button>
                                                            </Flex>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            type="default"
                                                            icon={selectedCredentialType === 'facebook_page'
                                                                ? <FacebookOutlined style={{ color: '#1877F2' }} />
                                                                : selectedCredentialType === 'instagram'
                                                                    ? <InstagramOutlined style={{ color: '#E4405F' }} />
                                                                    : <WhatsAppOutlined style={{ color: '#25D366' }} />
                                                            }
                                                            onClick={handleFacebookLogin}
                                                            block
                                                            data-tour="credential-login-btn"
                                                        >
                                                            {t('connect_with_platform', {
                                                                platform: credentialChannelTypes.find(ct => ct.value === selectedCredentialType)?.label || selectedCredentialType,
                                                                defaultValue: `Connect with ${credentialChannelTypes.find(ct => ct.value === selectedCredentialType)?.label || selectedCredentialType}`
                                                            })}
                                                        </Button>
                                                    )
                                                )}
                                            </Flex>
                                        </div>
                                    ) : (
                                        <Form.Item
                                            label={t('credential')}
                                            name="credential_id"
                                            rules={[{ required: true, message: t('please_select_credential') }]}
                                        >
                                            <Select
                                                placeholder={t('select_credential')}
                                                loading={credentialsLoading}
                                                options={credentialOptions}
                                                data-tour="channel-credential"
                                                onChange={() => {
                                                    channelForm.setFieldsValue({ external_channel_id: null });
                                                }}
                                                dropdownRender={(menu) => (
                                                    <>
                                                        {menu}
                                                        <Divider style={{ margin: '8px 0' }} />
                                                        <Button
                                                            type="text"
                                                            icon={<PlusOutlined />}
                                                            onClick={handleStartCredentialCreation}
                                                            block
                                                        >
                                                            {t('create_new_credential', { defaultValue: 'Create New Credential' })}
                                                        </Button>
                                                    </>
                                                )}
                                            />
                                        </Form.Item>
                                    )}
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={t('channel_name')}
                                        name="external_channel_id"
                                        rules={[{ required: true, message: t('please_select_resource') }]}
                                        tooltip={t('external_channel_id_tooltip', { defaultValue: 'Select the Page, Account, or Number you want to connect' })}
                                        data-tour="channel-resource"
                                    >
                                        <Select
                                            placeholder={selectedCredentialId ? t('select_page_or_account') : t('select_credential_first')}
                                            options={resourceOptions}
                                            loading={resourcesLoading}
                                            disabled={!selectedCredentialId}
                                            showSearch
                                            optionFilterProp="name"
                                            optionLabelProp="name"
                                        />
                                    </Form.Item>
                                </Col>



                                <Col xs={24}>
                                    <Flex gap="small" justify="flex-end">
                                        <Button
                                            onClick={handleCancelChannelCreation}
                                            disabled={channelCreating}
                                        >
                                            {t('cancel')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            onClick={handleCreateChannel}
                                            loading={channelCreating}
                                            data-tour="channel-create-submit"
                                        >
                                            {t('create_channel')}
                                        </Button>
                                    </Flex>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>
            )}

            {mode === 'edit' && (
                <Col xs={24}>
                    <Form.Item
                        label={t('admins')}
                        name="admins"
                        style={{ marginBottom: 4 }}
                        validateTrigger={['onChange']}
                        tooltip={t('admins_tooltip', { defaultValue: 'Select contacts who can receive escalated conversations' })}
                    >
                        <Select
                            mode="multiple"
                            placeholder={t('search_admins')}
                            loading={contactsLoading}
                            style={{ width: '100%' }}
                            options={renderAdminsOptions}
                            filterOption={false}
                            onSearch={handleContactsSearch}
                            onPopupScroll={handleContactsScroll}
                            showSearch
                            allowClear
                        />
                    </Form.Item>
                </Col>
            )}
        </Row>
    );
};

PlatformSection.propTypes = {
    form: PropTypes.object.isRequired,
    mode: PropTypes.oneOf(['edit', 'create']).isRequired,
    id: PropTypes.string,
    initialData: PropTypes.object,
    onPagesLoadingChange: PropTypes.func,
    onPagesReadyChange: PropTypes.func,
    typeRef: PropTypes.object,
    onChannelChange: PropTypes.func,
    onStartChannelCreation: PropTypes.func,
    onCancelChannelCreation: PropTypes.func,
};

export default PlatformSection;
